import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

/**
 * Phase 44 — global auth state for StoxTrade.
 *
 * Roles: GUEST (null user), USER, POWER_USER, ADMIN
 * Status: PENDING, ACTIVE, SOFT_DELETED
 * Subscription: NONE, TIPS, AUTO_TRADING
 *
 * The provider skips the /auth/me check when the URL fragment contains
 * `session_id=` so the AuthCallback can exchange the session first
 * (avoids the AuthProvider race condition documented in the playbook).
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      // AuthCallback will handle the session exchange first
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(null);
    window.location.href = "/";
  }, []);

  const value = {
    user,
    loading,
    refresh,
    logout,
    setUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === "ADMIN",
    isPowerUser: user?.role === "POWER_USER" || user?.role === "ADMIN",
    isActive: user?.status === "ACTIVE",
    isPending: user?.status === "PENDING",
    isSoftDeleted: user?.is_deleted === true,
    // 2026-09-13 — chat: "User should be able to see Stock Tips page
    // (without Paper trading)" — confirmed via follow-up question ("any
    // active user, no subscription needed"). Was gated behind a TIPS/
    // AUTO_TRADING subscription or POWER_USER/ADMIN role; now matches the
    // backend's own can_access_sections_1_to_4() (models/user.py), which
    // already just checks status === ACTIVE.
    hasTipsAccess: user?.status === "ACTIVE",
    hasAutoTradingAccess:
      user?.status === "ACTIVE" &&
      (user?.role === "ADMIN" || user?.role === "POWER_USER" ||
       user?.subscription === "AUTO_TRADING"),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
